// @ts-nocheck
(() => {
  const SECTION_SELECTOR = '.how-it-works[data-section-id]';
  const BREAKPOINT_MOBILE = 768;
  const SWIPER_RETRY_DELAY = 120;
  const SWIPER_MAX_RETRIES = 20;

  const parseCounterValue = (rawValue) => {
    const text = String(rawValue || '').trim();
    const match = text.match(/-?\d+(?:[.,]\d+)?/);
    if (!match || match.index === undefined) return null;

    const token = match[0];
    const tokenNormalized = token.replace(',', '.');
    const target = Number.parseFloat(tokenNormalized);
    if (Number.isNaN(target)) return null;

    const decimalPart = tokenNormalized.split('.')[1] || '';

    return {
      target,
      decimals: decimalPart.length,
      prefix: text.slice(0, match.index),
      suffix: text.slice(match.index + token.length),
      original: text
    };
  };

  const initStatCounters = (section) => {
    if (section.dataset.howItWorksCountersInitialized === 'true') return;
    section.dataset.howItWorksCountersInitialized = 'true';

    const statValues = Array.from(section.querySelectorAll('.how-it-works__stat-value'));
    if (!statValues.length) return;

    const counters = statValues
      .map((element) => {
        const parts = parseCounterValue(element.textContent || '');
        if (!parts) return null;
        return { element, parts };
      })
      .filter(Boolean);

    if (!counters.length) return;

    const setZeroValues = () => {
      counters.forEach(({ element, parts }) => {
        const zeroValue = parts.decimals > 0 ? Number(0).toFixed(parts.decimals) : '0';
        element.textContent = `${parts.prefix}${zeroValue}${parts.suffix}`;
      });
    };

    // Keep counters at 0 until the section enters viewport.
    setZeroValues();

    const setFinalValues = () => {
      counters.forEach(({ element, parts }) => {
        element.textContent = parts.original;
      });
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setFinalValues();
      return;
    }

    let animationRunId = 0;
    let hasAnimatedInCurrentView = false;

    const animate = () => {
      animationRunId += 1;
      const currentRunId = animationRunId;

      const duration = 2200;
      const startedAt = performance.now();

      const step = (now) => {
        if (currentRunId !== animationRunId) return;

        const progress = Math.min((now - startedAt) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);

        counters.forEach(({ element, parts }) => {
          const current = parts.target * eased;
          const rounded = parts.decimals > 0 ? current.toFixed(parts.decimals) : String(Math.round(current));
          element.textContent = `${parts.prefix}${rounded}${parts.suffix}`;
        });

        if (progress < 1) {
          requestAnimationFrame(step);
          return;
        }

        setFinalValues();
      };

      requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (hasAnimatedInCurrentView) return;
            hasAnimatedInCurrentView = true;
            animate();
            return;
          }

          // Reset when leaving viewport so counting can replay next time it enters.
          hasAnimatedInCurrentView = false;
          animationRunId += 1;
          setZeroValues();
        });
      },
      { threshold: 0.35 }
    );

    observer.observe(section);
  };

  const initBackgroundVideos = (section) => {
    const videos = section.querySelectorAll('[data-how-it-works-video]');
    if (!videos.length) return;

    const startMuted = section.dataset.startMuted === 'true';
    const autoplayOnViewport = section.dataset.autoplayOnViewport === 'true';
    const clickToUnmute = section.dataset.clickToUnmute === 'true';

    videos.forEach((video) => {
      video.muted = startMuted;
      video.playsInline = true;
      video.loop = true;

      if (clickToUnmute && video.dataset.unmuteBound !== 'true') {
        video.dataset.unmuteBound = 'true';
        video.addEventListener('click', () => {
          video.muted = !video.muted;
        });
      }
    });

    if (!autoplayOnViewport) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (!(video instanceof HTMLVideoElement)) return;

        if (entry.isIntersecting) {
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
          }
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.25 });

    videos.forEach((video) => observer.observe(video));
  };

  const initSection = (section) => {
    if (section.dataset.howItWorksInitialized === 'true') return;
    section.dataset.howItWorksInitialized = 'true';

    initBackgroundVideos(section);
    initStatCounters(section);

    const carousel = section.querySelector('[data-how-it-works-carousel]');
    const pagination = section.querySelector('[data-how-it-works-pagination]');
    if (!carousel || !pagination) return;

    let swiperInstance = null;
    let retryCount = 0;

    const enableAutoplay = section.dataset.enableAutoplay === 'true';
    const autoplaySeconds = Number(section.dataset.autoplaySpeed || '4');
    const autoplayDelay = Math.max(2, Math.min(10, autoplaySeconds)) * 1000;

    const destroySwiper = () => {
      if (!swiperInstance) return;
      swiperInstance.destroy(true, true);
      swiperInstance = null;
    };

    const createSwiper = () => {
      if (swiperInstance || window.innerWidth > BREAKPOINT_MOBILE) return;

      if (!window.Swiper) {
        if (retryCount < SWIPER_MAX_RETRIES) {
          retryCount += 1;
          window.setTimeout(createSwiper, SWIPER_RETRY_DELAY);
        }
        return;
      }

      const swiperConfig = {
        slidesPerView: 1.08,
        spaceBetween: 12,
        centeredSlides: false,
        watchOverflow: true,
        pagination: {
          el: pagination,
          clickable: true
        }
      };

      if (enableAutoplay) {
        swiperConfig.autoplay = {
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        };
      }

      swiperInstance = new window.Swiper(carousel, swiperConfig);
    };

    const updateSwiper = () => {
      if (window.innerWidth <= BREAKPOINT_MOBILE) {
        createSwiper();
      } else {
        destroySwiper();
      }
    };

    window.addEventListener('resize', updateSwiper);
    window.addEventListener('load', updateSwiper, { once: true });
    updateSwiper();
  };

  const init = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => initSection(section));
  };

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('theme:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
